import {
  CALLING_CODES,
  COUNTRIES,
  callingCode,
  countryForCallingCode,
} from '../constants/countries';
import type { Country } from '../constants/countries';

/** The digits of a number written with `+` or `00` in front (`+44 7911` and `0044 7911` → `447911`). */
const internationalDigits = (text: string): string =>
  text.replace(/\D/g, '').slice(text.startsWith('+') ? 0 : 2);

/** The calling code a string of digits opens with (they never start one another), or null. */
export function callingCodeOf(digits: string): string | null {
  for (const length of [1, 2, 3]) {
    const code = digits.slice(0, length);
    if (code.length === length && CALLING_CODES.has(code)) return code;
  }
  return null;
}

/**
 * Whether `digits` (E.164 without the `+`) can be a phone number: a real country calling code, then a national number
 * the way that country writes it — India 10 digits, the North American plan (+1) 10 digits with an area and an
 * exchange code that start 2–9; anywhere else at least 4 digits — and 15 digits at most in all (E.164).
 */
export function isValidInternationalDigits(digits: string): boolean {
  if (!/^[1-9]\d{6,14}$/.test(digits)) return false;
  const code = callingCodeOf(digits);
  if (code === null) return false;
  const national = digits.slice(code.length);
  if (code === '1') return /^[2-9]\d{2}[2-9]\d{6}$/.test(national);
  if (code === '91') return /^[1-9]\d{9}$/.test(national);
  return national.length >= 4;
}

/**
 * Phone normalisation, India-first (the firm is in India): 10-digit mobiles starting 6–9, with or
 * without 0 / 91 / +91, become `+91XXXXXXXXXX`. Other countries must be entered with `+` or `00`, and
 * the country calling code must be a real one with a number the way that country writes it
 * (`isValidInternationalDigits`). Returns null when the input cannot be interpreted — never guesses.
 */
export function normalizePhone(input: string): string | null {
  const cleaned = input.trim().replace(/[\s().-]/g, '');
  if (cleaned === '') return null;

  if (cleaned.startsWith('+') || cleaned.startsWith('00')) {
    const digits = cleaned.replace(/^\+|^00/, '');
    return isValidInternationalDigits(digits) ? `+${digits}` : null;
  }
  if (!/^\d+$/.test(cleaned)) return null;

  const national = cleaned.startsWith('0') ? cleaned.slice(1) : cleaned;
  if (/^[6-9]\d{9}$/.test(national)) return `+91${national}`;
  if (/^91[6-9]\d{9}$/.test(national)) return `+${national}`;
  return null;
}

export const isValidPhone = (input: string): boolean => normalizePhone(input) !== null;

/** `+919876543210` → `919876543210` (the shape `wa.me` expects). */
export const phoneDigits = (e164: string): string => e164.replace(/\D/g, '');

/** `+919876543210` → `+91 98765 43210` for display. */
export function formatPhoneDisplay(e164: string): string {
  const match = /^\+91(\d{5})(\d{5})$/.exec(e164);
  return match ? `+91 ${match[1] ?? ''} ${match[2] ?? ''}` : e164;
}

/** `91` → `+91`; `1876` → `+1 876` (how a country's dial code is shown). */
export function formatDial(dial: string): string {
  return dial.startsWith('1') && dial.length > 1 ? `+1 ${dial.slice(1)}` : `+${dial}`;
}

/**
 * The phone number the form sends, from the country the visitor chose and what they typed: `+` and the dial code
 * and the national digits. A number typed with its own `+` or `00` is taken as it is (`+44 7911 123456` is a British
 * number whatever country was picked). A national number may begin with the trunk `0` (`07911 …`) or, for India,
 * `91`; both are dropped. Nothing typed gives `''`. This only builds the string: whether it is a phone number is
 * `normalizePhone`'s decision, the same one the server makes.
 */
export function composePhone(dial: string, typed: string): string {
  const text = typed.trim();
  if (text === '') return '';
  if (text.startsWith('+') || text.startsWith('00')) {
    const digits = internationalDigits(text);
    return digits === '' ? '+' : `+${digits}`;
  }
  let digits = text.replace(/\D/g, '');
  if (digits === '') return '';
  if (dial === '91' && digits.length === 12 && digits.startsWith('91')) digits = digits.slice(2);
  if (callingCode(dial) !== '1' && digits.startsWith('0')) digits = digits.slice(1);
  return `+${dial}${digits}`;
}

export interface SplitPhone {
  country: Country;
  /** The national part, digits only (for `+1 876 …`, what follows the area code). */
  national: string;
}

/**
 * Which country an international number (`+919024780751`, `00447911123456`) belongs to and what is left of it, or null
 * when it does not open with a calling code. Of countries that share a code the first listed is taken (`+1` → the
 * United States); a Caribbean area code (`+1876`) picks its own country.
 */
export function splitPhone(value: string): SplitPhone | null {
  const text = value.trim();
  if (!text.startsWith('+') && !text.startsWith('00')) return null;
  const digits = internationalDigits(text);
  const code = callingCodeOf(digits);
  if (code === null) return null;
  const own = COUNTRIES.filter(
    (country) =>
      callingCode(country.dial) === code &&
      country.dial.length > code.length &&
      digits.startsWith(country.dial),
  ).sort((a, b) => b.dial.length - a.dial.length)[0];
  const country = own ?? countryForCallingCode(code);
  if (!country) return null;
  return { country, national: digits.slice(country.dial.length) };
}
